import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockDashboardService } from '@/services/mock/MockDashboardService';
import { DashboardOverviewData, DateRangePreset } from '@/types/domain/dashboard';
import { RoleSpecificDashboardData } from '@/types/domain/procurement-dashboard';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { MetricCard } from '@/components/data-display/MetricCard';
import { ProcurementSpendChart } from '@/components/dashboard/ProcurementSpendChart';
import { PipelineStatusCard } from '@/components/dashboard/PipelineStatusCard';
import { RecentActivityTable } from '@/components/dashboard/RecentActivityTable';
import { QuickActionsPanel } from '@/components/dashboard/QuickActionsPanel';
import { SupplierDeliveryHealthWidget } from '@/features/dashboard/components/SupplierDeliveryHealthWidget';
import { ExceptionCenterWidget } from '@/features/dashboard/components/ExceptionCenterWidget';
import { IntegrationHealthWidget } from '@/features/dashboard/components/IntegrationHealthWidget';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { SourceBadge } from '@/components/data-display/SourceBadge';
import { MOCK_AI_RECOMMENDATIONS } from '@/mocks/database';
import { FileText, CheckSquare, Layers, ShoppingBag, Bot } from 'lucide-react';
import { animateFadeIn } from '@/lib/motion';
import { usePermission } from '@/hooks/usePermission';
import { ROUTES } from '@/constants/routes';

export const DashboardPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, isAuditorReadOnly } = usePermission();

  const [preset, setPreset] = useState<DateRangePreset>('THIS_MONTH');
  const [data, setData] = useState<DashboardOverviewData | null>(null);
  const [enterpriseData, setEnterpriseData] = useState<RoleSpecificDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadDashboard = useCallback(
    async (rangePreset: DateRangePreset, showRefreshing = false) => {
      try {
        if (showRefreshing) setIsRefreshing(true);
        else setIsLoading(true);

        const [overviewRes, enterpriseRes] = await Promise.all([
          mockDashboardService.getDashboardOverview(rangePreset),
          mockDashboardService.getEnterpriseDashboard(
            { dateRangePreset: rangePreset, companyCode: 'ALL', plantCode: 'ALL', departmentCode: 'ALL' },
            user
          ),
        ]);

        setData(overviewRes);
        setEnterpriseData(enterpriseRes);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [user]
  );

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadDashboard(preset);
  }, [preset, loadDashboard]);

  const handleRefresh = () => {
    loadDashboard(preset, true);
  };

  const getKpiIcon = (iconName: string) => {
    switch (iconName) {
      case 'FileText':
        return FileText;
      case 'CheckSquare':
      case 'CheckCircle':
        return CheckSquare;
      case 'Layers':
        return Layers;
      case 'ShoppingBag':
        return ShoppingBag;
      default:
        return FileText;
    }
  };

  if (isLoading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3 font-heading">
        <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-xs font-bold text-foreground-secondary">
          Loading ProcureAI Enterprise Procurement Command Center...
        </p>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="space-y-6 pb-14 font-heading w-full">
      {/* 1. Dashboard Header with Uplighted New Requisition Action & Live Telemetry Strip */}
      <DashboardHeader
        selectedPreset={preset}
        onPresetChange={setPreset}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onCreateRequisition={!isAuditorReadOnly() ? () => navigate(ROUTES.REQUESTS.NEW) : undefined}
      />

      {/* 2. Top 4 KPI Metric Cards (Aligned Boxes) */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 items-stretch">
          {data.kpis.map((kpi, idx) => (
            <div key={kpi.id} className="h-full">
              <MetricCard
                title={kpi.label}
                value={kpi.value}
                description={kpi.subtitle}
                trend={kpi.trend}
                trendDirection={kpi.trendDirection}
                source={kpi.source}
                icon={getKpiIcon(kpi.iconName)}
                variant={idx === 0 ? 'heroEmerald' : 'default'}
              />
            </div>
          ))}
        </div>
      )}

      {/* 3. Main Analytics Section & Pipeline Status Panel (Aligned Boxes) */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2 h-full flex flex-col">
            <ProcurementSpendChart
              data={data.spendTrend}
              totalSpend={data.totalSpendFormatted}
              trendPercentage={data.spendTrendPercentage}
            />
          </div>
          <div className="h-full flex flex-col">
            <PipelineStatusCard
              stages={data.pipeline}
              onStageClick={() => navigate(ROUTES.REQUESTS.LIST)}
            />
          </div>
        </div>
      )}

      {/* 4. Recent Activity Table + Quick Actions & AI Recommendations (Aligned Boxes) */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2 h-full flex flex-col">
            <RecentActivityTable
              activities={data.recentActivity}
              onViewAll={() => navigate(ROUTES.REQUESTS.LIST)}
              onItemClick={(item) => {
                if (item.entityType === 'REQUISITION') navigate(ROUTES.REQUESTS.DETAILS(item.id));
                else navigate(ROUTES.REQUESTS.LIST);
              }}
            />
          </div>

          <div className="h-full flex flex-col space-y-6">
            {/* Quick Actions Panel */}
            <QuickActionsPanel />

            {/* AI Procurement Recommendation */}
            <Card className="border-copper/30 bg-copper/5 rounded-2xl shadow-2xs flex-1">
              <CardHeader className="pb-3 border-b border-copper/20">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-sm font-bold flex items-center gap-2 text-copper-dark dark:text-amber-300">
                    <Bot className="h-4 w-4" />
                    AI Procurement Insight
                  </CardTitle>
                  <SourceBadge source="AI GENERATED" showIcon={false} className="py-0 px-1.5 text-[9px]" />
                </div>
              </CardHeader>
              <CardContent className="pt-3 space-y-3">
                {MOCK_AI_RECOMMENDATIONS.slice(0, 1).map((rec: any) => (
                  <div key={rec.id} className="rounded-xl border border-border bg-card p-3.5 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-copper-dark dark:text-amber-300 uppercase tracking-wider">
                        {rec.recommendationType}
                      </span>
                      <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded">
                        {Math.round(rec.confidenceScore * 100)}% Confidence
                      </span>
                    </div>
                    <p className="font-heading text-xs font-bold text-foreground">{rec.title}</p>
                    <p className="text-[11px] text-foreground-secondary leading-relaxed">{rec.summary}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* 5. Additional Content Below: Commercial Health & Exception Control Center */}
      {enterpriseData?.supplierDeliveryHealth && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch pt-2">
          <div className="h-full flex flex-col">
            <SupplierDeliveryHealthWidget metrics={enterpriseData.supplierDeliveryHealth} />
          </div>
          <div className="h-full flex flex-col">
            <ExceptionCenterWidget exceptions={enterpriseData.exceptions} />
          </div>
        </div>
      )}

      {/* 6. Systems & SAP S/4HANA Simulator Connector Readiness */}
      {enterpriseData?.integrationHealth && (
        <div className="pt-2">
          <IntegrationHealthWidget metrics={enterpriseData.integrationHealth} />
        </div>
      )}
    </div>
  );
};
